/**
 * «Позже» на плашке «Вышла новая версия» (15.10.2026, решение владельца: напоминание раз в N дней, N = 3).
 *
 * Хранится в `localStorage` под учёткой устройства, а не ученика: обновляется приложение, а не человек.
 * Запись — `{version, at}`: КАКУЮ версию отложили и КОГДА.
 *
 * ⚠️ НОВАЯ ВЕРСИЯ СБРАСЫВАЕТ «ПОЗЖЕ». Отложили 1.1.2, а вышла 1.1.3 — плашка показывается сразу: человек
 * откладывал не её.
 *
 * ⚠️ СЛОМАННОЕ ХРАНИЛИЩЕ НЕ ПРЯЧЕТ ПЛАШКУ. Приватный режим, запрет записи, мусор в значении — показываем:
 * плашка лёгкая и закрывается одним нажатием, а молча спрятанное обновление — хуже.
 */

const LATER_KEY = 'onebilim.updateLater.v1';
const DAY_MS = 24 * 60 * 60 * 1000;

export type LaterRecord = { version: string; at: number };

function readLater(): LaterRecord | null {
  try {
    const raw = localStorage.getItem(LATER_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LaterRecord>;
    return typeof v?.version === 'string' && typeof v.at === 'number' && Number.isFinite(v.at)
      ? { version: v.version, at: v.at }
      : null;
  } catch {
    return null;
  }
}

/** «Позже» для этой версии: запомнить, когда отложили. */
export function rememberLater(version: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(LATER_KEY, JSON.stringify({ version, at: now }));
  } catch {
    /* хранилище закрыто — плашка просто появится снова при следующем возврате */
  }
}

/**
 * Показывать ли плашку для вышедшей версии `latest` сейчас.
 *
 * Отложенная та же версия молчит `days` суток с момента «Позже»; часы, переведённые назад (отложили «в будущем»),
 * тоже считаются истёкшим сроком — иначе плашка пропала бы на неопределённое время.
 */
export function shouldShowBanner(latest: string, now: number, days: number): boolean {
  return !laterHides(readLater(), latest, now, days);
}

/**
 * Прячет ли отметка «Позже» плашку версии `latest` сейчас: та же версия и срок `days` суток не вышел. Одна и та же
 * мерка для записи в хранилище и для отметки сеанса в `UpdateGate`.
 *
 * ⚠️ ОТМЕТКА СЕАНСА ТОЖЕ СО СРОКОМ (симулятор стенда, 17.10.2026). Раньше «Позже» в памяти прятало плашку до конца
 * процесса: приложение, которое iOS днями держит приостановленным, не напоминало никогда, хотя запись в хранилище
 * давно истекла.
 */
export function laterHides(later: LaterRecord | null, latest: string, now: number, days: number): boolean {
  if (!later || later.version !== latest) return false;
  const elapsed = now - later.at;
  return elapsed >= 0 && elapsed < days * DAY_MS;
}
